import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Grid, Header, Message, Segment } from 'semantic-ui-react';
import { Button, Dropdown, Form } from 'formik-semantic-ui';
import { EnumEx, UserGroupLabels, IAssignee, EUserGroup } from '@relab/alstom-ptw-shared';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { ActionType, RootState, ServerRequestStatus } from '../redux/types';
import { appState, appStateServerRequests } from '../redux/selectors';
import { assigneeAddAction } from '../redux/actions';
import { UserSelectComponent } from './UserSelectComponent';
import { TranslationComponent } from './TranslationComponent';

const roles = EnumEx.getNames(EUserGroup).map((userGroup) => ({
  key: userGroup,
  text: UserGroupLabels[userGroup],
  value: userGroup,
}));

const CreateAssigneeSchema = (t: typeof i18n.t) =>
  Yup.object().shape({
    user: Yup.string().required(t('User is required')).ensure(),
    group: Yup.string().required(t('Role is required')).ensure(),
  });

interface AssigneeCreateComponentProps {
  projectId: string;
  assigneeAddAction: any;
  createAssigneeServerRequest?: ServerRequestStatus;
}

const PAssigneeCreateComponent = ({
  projectId,
  assigneeAddAction,
  createAssigneeServerRequest,
}: AssigneeCreateComponentProps) => {
  const handleSubmit = ({ user, group }: Partial<IAssignee>) => {
    assigneeAddAction(projectId, {
      user,
      group: EUserGroup[group],
    });
  };

  const { t } = useTranslation();

  const validationSchema = useMemo(() => CreateAssigneeSchema(t), [t]);

  const initialValues: Partial<IAssignee> = {
    user: null,
    group: null,
  };

  return (
    <Form
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      ignoreLoading
      render={({ resetForm }) => (
        <Segment>
          <Header>
            <TranslationComponent stringKey={'New Assignee'} />
          </Header>

          <Grid columns="equal">
            <Grid.Row>
              <Grid.Column>
                <UserSelectComponent name="user" />
                <ErrorMessage name="user">{(msg) => <Message error content={msg} />}</ErrorMessage>
              </Grid.Column>
              <Grid.Column>
                <Dropdown name="group" options={roles} />
                <ErrorMessage name="group">{(msg) => <Message error content={msg} />}</ErrorMessage>
              </Grid.Column>
              <Grid.Column>
                <Button.Submit primary fluid size="large">
                  <TranslationComponent stringKey={'Add'} />
                </Button.Submit>
                {createAssigneeServerRequest && createAssigneeServerRequest.success && (
                  <React.Fragment>
                    <Message content={t('Assignee created')} />
                    <Button fluid size="large" onClick={() => resetForm()}>
                      Reset
                    </Button>
                  </React.Fragment>
                )}
                {createAssigneeServerRequest && createAssigneeServerRequest.error && (
                  <Message error content={createAssigneeServerRequest.error} />
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      )}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  projectId: appState(state).project._id,
  createAssigneeServerRequest: appStateServerRequests(state, ActionType.ASSIGNEE_ADD),
});

export const AssigneeCreateComponent = connect(mapStateToProps, {
  assigneeAddAction,
})(PAssigneeCreateComponent);
